/**
 * Edit Asset Modal
 *
 * Wraps `AssetDetailsStep` (the wizard's edit form) in a reactstrap Modal
 * so users can update an asset without leaving the dashboard. The
 * underlying `useUpdateAssetMutation` invalidates the asset cache so the
 * embedded `AssetDetailSection` refetches automatically on save.
 */

import { useEffect, useState } from 'react';
import { Loader2, Save, X } from 'lucide-react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { toastFromError } from '@g17eco/molecules/toasts';
import { AssetDetailsStep } from '../form/AssetDetailsStep';
import { useUpdateAssetMutation } from '../../api/emissions';
import {
  AssetType,
  type CreateAssetRequest,
  type EmissionAsset,
  type UpdateAssetRequest,
} from '../../types/emissions';

interface EditAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  initiativeId: string;
  asset: EmissionAsset;
}

const assetToCreateRequest = (asset: EmissionAsset): CreateAssetRequest => {
  const base: CreateAssetRequest = {
    name: asset.name,
    assetType: asset.assetType,
    description: asset.description,
    address: asset.address,
    region: asset.region,
    unitCount: asset.unitCount,
    customIntensityValue: asset.customIntensityValue,
    customIntensityLabel: asset.customIntensityLabel,
  };
  switch (asset.assetType) {
    case AssetType.Facility:
    case AssetType.ManufacturingFacility:
      base.floorAreaM2 = asset.floorAreaM2;
      base.employeeCount = asset.employeeCount;
      break;
    case AssetType.Vehicle:
      base.vehicleType = asset.vehicleType;
      base.identifier = asset.identifier;
      break;
    case AssetType.DataCentre:
      base.pue = asset.pue;
      base.itLoadKw = asset.itLoadKw;
      base.rackCount = asset.rackCount;
      break;
    case AssetType.Equipment:
      base.operatingHoursPerYear = asset.operatingHoursPerYear;
      base.ratedCapacityKw = asset.ratedCapacityKw;
      break;
  }
  return base;
};

const toUpdateRequest = (asset: CreateAssetRequest): UpdateAssetRequest => {
  const { assetType: _ignored, ...rest } = asset;
  return rest;
};

export const EditAssetModal = ({ isOpen, onClose, initiativeId, asset }: EditAssetModalProps) => {
  const [draft, setDraft] = useState<CreateAssetRequest>(() => assetToCreateRequest(asset));
  const [updateAsset, { isLoading: isSaving }] = useUpdateAssetMutation();

  // Re-seed when the modal is reopened or the underlying asset changes.
  useEffect(() => {
    if (isOpen) {
      setDraft(assetToCreateRequest(asset));
    }
  }, [isOpen, asset]);

  const handleSave = async () => {
    try {
      await updateAsset({
        initiativeId,
        assetId: asset._id,
        data: toUpdateRequest(draft),
      }).unwrap();
      onClose();
    } catch (error) {
      toastFromError(error);
    }
  };

  const canSave = draft.name.trim().length > 0;

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size='lg' scrollable>
      <ModalHeader toggle={onClose}>Edit Asset</ModalHeader>
      <ModalBody>
        <AssetDetailsStep
          asset={draft}
          onUpdate={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
          noOuterCard
        />
      </ModalBody>
      <ModalFooter>
        <Button variant='outline' onClick={onClose} disabled={isSaving}>
          <X className='tw:size-4 tw:mr-1' />
          Cancel
        </Button>
        <Button
          className='tw:bg-emerald-700 tw:hover:bg-emerald-800 tw:text-white tw:border-0'
          onClick={handleSave}
          disabled={!canSave || isSaving}
        >
          {isSaving ? (
            <>
              <Loader2 className='tw:size-4 tw:mr-1 tw:animate-spin' />
              Saving...
            </>
          ) : (
            <>
              <Save className='tw:size-4 tw:mr-1' />
              Save Changes
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
